/**
 * Thin API client for the Sprint 2 POS endpoints.
 *
 * Adds the two headers every mutating request needs:
 *   • Authorization: Bearer <pos_token>   — POS session (from PIN login)
 *   • X-Pos-Terminal: <terminal_id>       — from the device binding
 *
 * And mints a fresh `event_id` for each call. The event_id is the
 * idempotency key on `pos_events` (contract §8); passing the same
 * value twice returns the cached response rather than double-applying.
 */
import axios from "axios";
import { getBinding, newEventId } from "./terminalBinding";

const API = process.env.REACT_APP_BACKEND_URL;

function bearer() {
  const t = window.localStorage.getItem("pos_token");
  return t ? `Bearer ${t}` : "";
}

function headers(extra = {}) {
  const b = getBinding();
  return {
    Authorization: bearer(),
    "X-Pos-Terminal": b?.terminal_id || "unbound",
    "Content-Type": "application/json",
    ...extra,
  };
}

/** Every mutating call carries an event_id — either freshly minted or
 *  supplied by the caller (so a retry uses the same key). Caller-
 *  supplied values ALWAYS win over the auto-mint. */
function withEvent(body = {}) {
  if (body && body.event_id) return body;
  return { ...body, event_id: newEventId() };
}

// ─── Business day + shift ────────────────────────────────────────────
export async function openBusinessDay(business_date, event_id) {
  const r = await axios.post(`${API}/api/pos/business-days/open`,
    withEvent({ business_date, event_id }), { headers: headers() });
  return r.data;
}
export async function closeBusinessDay(event_id) {
  const r = await axios.post(`${API}/api/pos/business-days/close`,
    withEvent({ event_id }), { headers: headers() });
  return r.data;
}
export async function openShift(opening_float = 0, event_id) {
  // NOTE — the server's `ShiftOpenIn` rejects any extra field via
  // `model_config = {"extra": "forbid"}` (see backend/routes/pos_orders.py
  // ShiftOpenIn). Sprint 2 correction B moved cash to the till entity,
  // so `opening_float` is intentionally unsupported here. Passing it
  // returned a 422 that killed the shift-open call — the reducer's
  // optimistic SHIFT_OPENED then made the UI claim "shift open" while
  // the server had no shift row, and the very next open-table request
  // hit "No open shift for this outlet". The parameter stays in the
  // signature only because a Till-open path elsewhere uses the same
  // name; it is deliberately NOT forwarded to the server.
  const r = await axios.post(`${API}/api/pos/shifts/open`,
    withEvent({ event_id }), { headers: headers() });
  return r.data;
}
export async function closeShift(event_id) {
  // Server-side ShiftCloseIn accepts ONLY event_id (+ optional
  // terminal_id from headers). Sending declared_cash produces a
  // 422 validation array. Cash lives on Till Close.
  const r = await axios.post(`${API}/api/pos/shifts/close`,
    withEvent({ event_id }), { headers: headers() });
  return r.data;
}

// ─── Orders ─────────────────────────────────────────────────────────
export async function openOrder(payload) {
  const r = await axios.post(`${API}/api/pos/orders`,
    withEvent(payload), { headers: headers() });
  return r.data.order;
}
// POS Cleanup Pass 3 · canonical unresolved-orders fetch used by the
// BusinessDayCloseDialog. Same server-side predicate the actual
// close_business_day route uses to decide whether to refuse the
// close, so the UI and the API can never disagree.
export async function fetchUnresolvedOrders(business_date) {
  const params = business_date ? { business_date } : {};
  const r = await axios.get(`${API}/api/pos/orders/unresolved`,
    { headers: headers(), params });
  return r.data;
}

export async function listOpenOrders(business_date) {
  // POS Cleanup Pass 1 · scope every hydration query to the currently
  // OPEN business date. An OPEN order from a prior date MUST NOT
  // rehydrate into today's session — that's the "old business day
  // occupying today's floor" bug. Caller passes the business_date
  // from `getSessionStatus()`; if it is falsy we return [] instead
  // of pulling the whole tenant history.
  if (!business_date) return [];
  const params = (state) => ({ state, business_date });
  const [openR, printedR, settledR] = await Promise.all([
    axios.get(`${API}/api/pos/orders`, { headers: headers(), params: params("OPEN") }),
    axios.get(`${API}/api/pos/orders`, { headers: headers(), params: params("PRINTED") }),
    axios.get(`${API}/api/pos/orders`, { headers: headers(), params: params("SETTLED") }),
  ]);
  return [
    ...(openR.data.orders || []),
    ...(printedR.data.orders || []),
    ...(settledR.data.orders || []),
  ];
}
export async function releaseDraftOrder(orderId, reason, event_id) {
  // POS Cleanup Pass 1 · non-transactional draft discard. Caller sends
  // the reason (EMPTY_BACK / UNSENT_DRAFT_RELEASE / EMPTY_TIMEOUT).
  // Server rejects if any ticket/invoice/tender exists on the order.
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/release-draft`,
    withEvent({ event_id, reason }), { headers: headers() });
  return r.data;
}
export async function voidOrder(orderId, reason, authorised_by_staff_id, event_id) {
  // POS Cleanup Pass 1 · canonical whole-order void. Manager PIN was
  // validated at the UI layer; we forward the authoriser id + reason.
  // Server cascades to invoice void (if any) and marks every line
  // voided_at atomically.
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/void`,
    withEvent({ event_id, reason, authorised_by_staff_id }), { headers: headers() });
  return r.data;
}
export async function getOrder(id) {
  const r = await axios.get(`${API}/api/pos/orders/${id}`, { headers: headers() });
  return r.data.order;
}
export async function addLine(orderId, payload) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/lines`,
    withEvent(payload), { headers: headers() });
  return r.data;
}
export async function fireTicket(orderId, event_id) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/tickets`,
    withEvent({ event_id }), { headers: headers() });
  return r.data;
}
export async function holdOrder(orderId, reason = "", event_id) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/hold`,
    withEvent({ reason, event_id }), { headers: headers() });
  return r.data.order;
}
export async function resumeOrder(orderId, event_id) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/resume`,
    withEvent({ event_id }), { headers: headers() });
  return r.data.order;
}
export async function transferTable(orderId, to_table_id, event_id) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/transfer-table`,
    withEvent({ to_table_id, event_id }), { headers: headers() });
  return r.data.order;
}
export async function getSessionStatus() {
  const r = await axios.get(`${API}/api/pos/session-status`, { headers: headers() });
  return r.data;
}
// ─── Tills · Sprint 2 B.2 ───────────────────────────────────────────
export async function getCurrentTill() {
  const r = await axios.get(`${API}/api/pos/tills/current`, { headers: headers() });
  return r.data.till;
}
export async function openTill(opening_float, event_id) {
  const r = await axios.post(`${API}/api/pos/tills/open`,
    withEvent({ opening_float, event_id }), { headers: headers() });
  return r.data.till;
}
export async function closeTill(declared_cash, event_id, extras = {}) {
  // POS Control Sprint · till close now carries the full
  // reconciliation payload. `extras` may include:
  //   till_id, close_method, manager_authorised_by_staff_id,
  //   takeover_reason, denominations, counted_cash, expected_cash,
  //   cash_variance, variance_reason, variance_note,
  //   settlement_mode_summary, float_returned.
  const r = await axios.post(`${API}/api/pos/tills/close`,
    withEvent({ declared_cash, event_id, ...extras }), { headers: headers() });
  return r.data;
}
export async function listTills() {
  // POS Control Sprint · list open tills at the caller's outlet
  // for the current shift, with per-till settlement summary attached.
  const r = await axios.get(`${API}/api/pos/tills`, { headers: headers() });
  return r.data.tills || [];
}
export async function listClosedTills() {
  // POS Control Sprint · list CLOSED tills for the current shift so
  // Shift Close's summary ledger reads persistent server state, not
  // an ephemeral in-orchestrator array.
  const r = await axios.get(`${API}/api/pos/tills/closed`, { headers: headers() });
  return r.data.tills || [];
}
export async function tillTakeoverLock(till_id, extras = {}) {
  // Manager PIN was already validated by the shared auth flow before
  // this call — the endpoint only records the takeover_active_at
  // timestamp so the cashier terminal cannot slip another tender in.
  const r = await axios.post(`${API}/api/pos/tills/${till_id}/takeover-lock`,
    withEvent({ ...extras, event_id: extras.event_id || `takeover_${till_id}_${Date.now()}` }),
    { headers: headers() });
  return r.data;
}
export async function managerDeclareCash(handling_cash, opening_float, event_id) {
  // POS Cleanup Pass 2 · The manager cash decision prompt was removed
  // — managers now use the same TillFloatPrompt as cashiers, no
  // "handling cash?" gate. This wrapper is retained only so any
  // straggler caller receives a no-op instead of a 404. Do NOT wire
  // this into new UI.
  return { ok: true, till: null };
}

// ─── Tables ─────────────────────────────────────────────────────────
export async function listTables() {
  const r = await axios.get(`${API}/api/pos/tables`, { headers: headers() });
  return r.data.tables || [];
}

// ─── Invoice / Tender / Settlement · Sprint 3 fiscal path ──────────
export async function getInvoiceConfig(outletId) {
  const r = await axios.get(
    `${API}/api/pos/outlets/${outletId}/invoice-config`,
    { headers: headers() });
  return r.data.invoice_config;
}
export async function printBill(orderId, series, event_id) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/print-bill`,
    withEvent({ series, event_id }), { headers: headers() });
  return r.data;   // { invoice, order }
}
export async function reprintBill(orderId, event_id, extras = {}) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/reprint-bill`,
    withEvent({ event_id, ...extras }), { headers: headers() });
  return r.data;   // { invoice }
}

// ─── Split order · POS Control Sprint (Feb 2026) ────────────────────
// Persists a bill split on the server. Returns { order (parent),
// children[] } — every child is a real pos_orders document with a
// real id, so subsequent printBill / settle / reprint calls resolve.
// `groups` = [{ line_ids: [...], label?: "..." }]
export async function splitOrder(orderId, groups, event_id) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/split`,
    withEvent({ event_id, groups }), { headers: headers() });
  return r.data;   // { order, children }
}
export async function voidInvoice(invoice_no, { reason, authorised_by_staff_id, event_id }) {
  // invoice_no contains slashes (e.g. RB/25-26/0001). Path is encoded
  // at the caller level via encodeURI; the server route uses :path.
  const r = await axios.post(
    `${API}/api/pos/invoices/${invoice_no}/void`,
    withEvent({ reason, authorised_by_staff_id, event_id }),
    { headers: headers() });
  return r.data;
}
export async function transferInvoiceToDues(invoice_no,
    { reason_type, reason_note, party_name, authorised_by_staff_id, event_id }) {
  const r = await axios.post(
    `${API}/api/pos/invoices/${invoice_no}/transfer-to-dues`,
    withEvent({ reason_type, reason_note, party_name,
                authorised_by_staff_id, event_id }),
    { headers: headers() });
  return r.data;
}
export async function addTender(orderId, tender, event_id) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/tenders`,
    withEvent({ ...tender, event_id }), { headers: headers() });
  return r.data;   // { tender, tenders, invoice_total, tendered_total, balance_due }
}
export async function reverseTender(tender_id, { reason, authorised_by_staff_id, event_id }) {
  const r = await axios.post(`${API}/api/pos/tenders/${tender_id}/reverse`,
    withEvent({ reason, authorised_by_staff_id, event_id }), { headers: headers() });
  return r.data;
}
export async function settleOrder(orderId, event_id) {
  const r = await axios.post(`${API}/api/pos/orders/${orderId}/settle`,
    withEvent({ event_id }), { headers: headers() });
  return r.data;   // { order, invoice, tenders, ... }
}
export async function listOrderTenders(orderId) {
  // The server returns tenders alongside settle/tender calls. For
  // hydration we drop through /pos/tenders?order_id=... — but a simpler
  // path is to include tenders in each order via list_orders. For now
  // we use the settlement report endpoint as a shortcut.
  const r = await axios.get(`${API}/api/pos/orders/${orderId}`, { headers: headers() });
  return r.data.order?.tenders || [];
}
